import { ErrorResponse, PointLog, ServiceResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useRef, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';

type PointLogContextType = {
  pointLogs: PointLog[];
  isLoading: boolean;
  error: ErrorResponse | null;
  canLoadMore: boolean;
  actions: {
    fetchPointLogs: (month: string, type: 'earn' | 'burn', top: number) => Promise<ServiceResponse<PointLog[]>>;
    loadMore: () => Promise<ServiceResponse<PointLog[]>>;
  };
};

const PointLogContext = createContext({
  pointLogs: [],
  isLoading: false,
  error: null,
  canLoadMore: false,
  actions: {
    fetchPointLogs: async () => ({ type: 'client-error', message: 'Not implemented' }),
    loadMore: async () => ({ type: 'client-error', message: 'Not implemented' }),
  },
} as PointLogContextType);

export function usePointLogContext() {
  return useContext(PointLogContext);
}

export const PointLogProvider = ({ children }: { children: React.ReactNode }) => {
  const [pointLogs, setPointLogs] = useState<PointLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);
  const [canLoadMore, setCanLoadMore] = useState(false);

  const { bzbsService } = useBuzzebeesServiceContext();

  const lastRowKey = useRef<string | undefined>(undefined);
  const currentMonth = useRef<string | undefined>(undefined);
  const currentType = useRef<'earn' | 'burn'>('earn');
  const currentTop = useRef<number>(10);

  const fetchPointLogs = async (
    month: string,
    type: 'earn' | 'burn',
    top: number
  ): Promise<ServiceResponse<PointLog[]>> => {
    currentMonth.current = month;
    currentType.current = type;
    currentTop.current = top;
    lastRowKey.current = undefined;

    setIsLoading(true);
    const response = await bzbsService.pointLogApi.getPointLog({
      month: month,
      type: type,
      top: top,
      lastRowKey: undefined,
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setPointLogs(response.model);
      setError(null);
      if (response.model.length === top) {
        setCanLoadMore(true);
        lastRowKey.current = response.model[response.model.length - 1].RowKey;
      }
    } else {
      setError(response);
    }

    return response;
  };

  const loadMore = async (): Promise<ServiceResponse<PointLog[]>> => {
    if (!canLoadMore) {
      return Promise.reject('No more data');
    }

    if (currentMonth.current === undefined) {
      return Promise.reject('No month selected');
    }

    setIsLoading(true);
    const response = await bzbsService.pointLogApi.getPointLog({
      month: currentMonth.current!,
      type: currentType.current!,
      top: currentTop.current,
      lastRowKey: lastRowKey.current,
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setPointLogs([...pointLogs, ...response.model]);
      if (response.model.length < currentTop.current) {
        setCanLoadMore(false);
      } else {
        setCanLoadMore(true);
        lastRowKey.current = response.model[response.model.length - 1].RowKey;
      }
    } else {
      setError(response);
    }

    return response;
  };

  return (
    <PointLogContext.Provider
      value={{
        pointLogs,
        isLoading,
        error,
        canLoadMore,
        actions: {
          fetchPointLogs,
          loadMore,
        },
      }}
    >
      {children}
    </PointLogContext.Provider>
  );
};
